import { ErrorMessage } from '@hookform/error-message';
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { Label, Text } from '@src/components/Typography';
import { VStack } from '@src/components/VStack';
import { palette } from '@src/theme';
import identity from 'lodash/identity';
import isNil from 'lodash/isNil';
import React from 'react';
import { Controller, get, useFormContext } from 'react-hook-form';
import { BASE_TEXT_FIELD_HEIGHT } from '../TextField/util';
import { SingleSelectFieldProps } from './interface';

const FONT_SIZE = '14px';

export const SingleSelectField = ({
  disabled,
  formatOnChange = identity,
  label,
  labelStyle = 'stacked',
  name,
  options,
  parseValue = identity,
  placeholder = 'Select an option',
}: SingleSelectFieldProps) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const fieldErrors = get(errors, name);
  const parsedErrors = fieldErrors ? { [name]: fieldErrors } : {};
  const hasErrors = !!parsedErrors[name];

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, value: fieldValue } }) => (
        <VStack>
          {labelStyle === 'stacked' && <Label size="s">{label}</Label>}
          <FormControl fullWidth sx={{ height: `${BASE_TEXT_FIELD_HEIGHT}px` }}>
            {(labelStyle === 'inline' || !fieldValue) && (
              <InputLabel
                id={`${name}-select-label`}
                disableAnimation={true}
                shrink={labelStyle === 'inline'}
                sx={
                  labelStyle === 'stacked'
                    ? {
                        fontSize: FONT_SIZE,
                        height: `${BASE_TEXT_FIELD_HEIGHT}px`,
                        left: '-5px',
                        top: '-3px',
                        color: palette.controls.outline,
                      }
                    : {}
                }
              >
                {labelStyle === 'inline' ? label : placeholder}
              </InputLabel>
            )}
            <Select
              disabled={disabled}
              displayEmpty={true}
              error={!!hasErrors}
              fullWidth
              label={labelStyle === 'inline' ? label : undefined}
              labelId={`${name}-select-label`}
              notched={labelStyle === 'inline'}
              SelectDisplayProps={{
                style: {
                  border: 'none',
                  color: !fieldValue ? palette.controls.outline : undefined,
                  fontSize: FONT_SIZE,
                },
              }}
              onChange={(e) => {
                const val = e.target.value;
                const newVal = formatOnChange(val, fieldValue);
                onChange(newVal);
              }}
              sx={{
                height: `${BASE_TEXT_FIELD_HEIGHT}px`,
              }}
              value={parseValue(fieldValue)}
              renderValue={(value: any) => {
                // Inline must render the placeholder itself since we don't use label shrink
                if (labelStyle === 'inline' && !value) {
                  return placeholder;
                }

                // For stacked style or a selected value, render the option label
                const selectedOption = options.find((option) => option.value === value);
                return selectedOption ? selectedOption.label : '';
              }}
            >
              {options.map((option) => (
                <MenuItem key={option.value} value={option.value} sx={{ fontSize: FONT_SIZE }}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <ErrorMessage
            name={name}
            errors={parsedErrors}
            render={({ message }) => (
              <Text size="s" color="state.error.secondary">
                {message}
              </Text>
            )}
          />
        </VStack>
      )}
    />
  );
};
